import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableDeletionHelper } from 'components/ApiTable/helpers/useApiTableDeletionHelper';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { produce } from 'immer';
import { type FC, memo, useMemo, useRef, useState } from 'react';
import { apiEndpointToBaseUrl } from 'utils/apiEndpointToFullUrl';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { useResponsiveTableButton } from '../../../../../features/DiscoverySettingsForms/utils/useResponsiveTableButton';
import {
  type FormValues,
  JumphostDialog,
  type JumphostLoginType,
} from './JumphostDialog';

import type { PostTablesSettingsJumphosts200DataItem } from '@ipfabric/api-client/client/schemas/postTablesSettingsJumphosts200DataItem';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useLocale } from 'containers/App/LocaleContext';

type JumphostSubmitData = {
  enabled?: boolean;
  label?: string;
  address?: string;
  port?: string | null;
  subnets?: string[];
  exclude?: string[];
  loginType?: JumphostLoginType;
  username?: string;
  password?: string;
};

const jumphostApiEndpoint = '/tables/settings/jumphosts';

const BareJumphost: FC = () => {
  const {
    settings: {
      globalDiscoveryConfiguration: { jumphost: jumphostMsg },
    },
  } = useLocale();
  const [editedJumphost, setEditedJumphost] = useState<
    PostTablesSettingsJumphosts200DataItem | 'new' | null
  >(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const confirmDialog = useSimpleConfirmDialog();
  const api = useApi();
  const notifications = useNotifications();
  const deletionHelper = useApiTableDeletionHelper({
    confirmDialog,
    confirmDialogMessages: jumphostMsg.deleteJumphostConfirmDialog,
    getApiTableModel: () => jumphostsApiTablePage.apiTableModel,
    deleteOne: (id: string) =>
      api.request({
        method: 'DELETE',
        url: `/settings/jumphosts/${id}`,
      }),
  });

  const bulkActions = useMemo(
    (): TableBulkAction[] => [deletionHelper.deleteBulkAction],
    [deletionHelper.deleteBulkAction],
  );

  const endpoint = useTableEndpoint({
    apiEndpoint: jumphostApiEndpoint,
    alternative: (originalEndpoint) => ({
      id: 'jumphost',
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: jumphostMsg.columns.action,
          webDependencies: ['id'],
          render: (rowData: PostTablesSettingsJumphosts200DataItem) => (
            <div>
              <TableButton
                icon='fa-pen'
                onClick={() => setEditedJumphost(rowData)}
                variant='plain'
              />
              {/** always get id from backend */}
              {deletionHelper.renderDeleteButton(rowData.id!)}
            </div>
          ),
        }),
      ],
      // load all columns because we use loaded data to fetch form values
      webDependencies: originalEndpoint.columns.map(prop('key')),
    }),
  });

  const { actionItem: downloadActionItem } = useResponsiveTableButton({
    label: 'Download public key',
    icon: 'fa-file-download',
    onClick: () => {
      const link = document.createElement('a');
      link.href = apiEndpointToBaseUrl('/settings/jumphosts/ssh-cert');
      link.click();
    },
    containerRef: tableContainerRef,
    size: 'small',
    variant: 'outline',
  });

  const menuItems = useMemo(
    () =>
      [
        {
          standalone: true,
          elementWithKey: (
            <Button
              key='new'
              variant='outline'
              size='small'
              icon='fa-plus-circle'
              onClick={() => {
                setEditedJumphost('new');
              }}
              type='button'
            >
              {jumphostMsg.btnAdd}
            </Button>
          ),
        } satisfies TableActionsItem,
        downloadActionItem,
      ].filter(Boolean),
    [downloadActionItem, jumphostMsg.btnAdd],
  );

  const jumphostsApiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      bulkActions,
      loading: deletionHelper.loading,
      rowIdField: 'id',
      menuItems,
    },
  });

  const saveJumphost = async (
    formValues: FormValues,
    successMessage: string,
    saveFn: (data: JumphostSubmitData) => Awaitable<void>,
  ) => {
    try {
      const { username, password, loginType } = formValues;
      const data = produce<JumphostSubmitData>(
        {
          enabled: formValues.enabled,
          label: formValues.label,
          address: formValues.address,
          port: formValues.port,
          subnets: formValues.subnets.ips,
          exclude: formValues.exclude.ips,
          loginType,
        } as JumphostSubmitData,
        (draft) => {
          if (username) {
            draft.username = username;
          }
          if (loginType === 'credentials' && password) {
            draft.password = password;
          }
          // if port is empty or returned as empty string from the input, set it to null
          const formPort = formValues.port;
          if (formPort === '') {
            draft.port = null;
          }
        },
      );
      await saveFn(data);
      setEditedJumphost(null);
      notifications.showNotification({
        type: 'success',
        message: successMessage,
      });
      jumphostsApiTablePage.apiTableModel.dispatch(reloadTableData);
    } catch (e) {
      if (isIpfError(e) && e.code === 'API_RESOURCE_CONFLICT') {
        notifications.showNotification({
          type: 'warning',
          message: jumphostMsg.request.resourceConflict,
        });
      } else if (isIpfError(e) && e.code === 'API_JUMPHOST_FORBIDDEN_SUBNET') {
        notifications.showNotification({
          type: 'danger',
          message: (e as any)?.message ?? 'Save failed',
        });
      } else {
        // must throw asynchronously, otherwise redux form swallows it and
        // crashes with different irrelevant error
        setTimeout(() => {
          throw e;
        }, 0);
      }
    }
  };

  const makeUpdateJumphost = (id: string) => (data: JumphostSubmitData) =>
    api.request({
      url: `/settings/jumphosts/${id}`,
      method: 'PATCH',
      data,
    });

  const handleUpdateJumphostSubmit = useStableCallback(
    (jumphostId: string) => async (formValues: FormValues) =>
      await saveJumphost(
        formValues,
        jumphostMsg.request.updateSuccess,
        // always get id from backend
        makeUpdateJumphost(jumphostId),
      ),
  );
  const addJumphost = async (data: JumphostSubmitData) =>
    await api.request({
      url: '/settings/jumphosts',
      method: 'POST',
      data,
    });

  const handleAddJumphostSubmit = useStableCallback((formValues: FormValues) =>
    saveJumphost(formValues, jumphostMsg.request.saveSuccess, addJumphost),
  );

  return (
    <div className='flex flex-col flex-1 gap' ref={tableContainerRef}>
      <div className='text-level-4 font-heading font-semibold'>Jumphosts</div>
      <ApiTablePageView model={jumphostsApiTablePage} />
      {confirmDialog.render()}
      {(() => {
        if (editedJumphost === 'new') {
          return (
            <JumphostDialog
              onClose={() => {
                setEditedJumphost(null);
              }}
              type='add'
              onSubmit={handleAddJumphostSubmit}
            />
          );
        }
        if (editedJumphost !== null) {
          return (
            <JumphostDialog
              onClose={() => {
                setEditedJumphost(null);
              }}
              type='edit'
              initialValues={editedJumphost}
              onSubmit={handleUpdateJumphostSubmit(editedJumphost.id!)}
            />
          );
        }
        return null;
      })()}
    </div>
  );
};

export const Jumphost = memo(BareJumphost);
